import { useEffect, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import {
  Store, Calendar, Users, Star, Clock, Check, X, DollarSign, Edit3, Save, Plus,
  Eye, ArrowLeft, Sparkles, BarChart3, TrendingUp, Grid3X3, Bell, ChevronRight,
  Zap, Table2
} from "lucide-react";
import { format, subDays, startOfMonth, endOfMonth, eachDayOfInterval, isFuture, parseISO } from "date-fns";
import { Link } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from "recharts";

const allCities = ["Bangalore", "Mumbai", "Delhi", "Hyderabad", "Pune", "Goa", "Chennai", "Kolkata", "Jaipur", "Ahmedabad"];
const categories = ["lounge", "nightclub", "bar", "brewpub", "rooftop", "cocktail-bar", "beach-bar", "speakeasy", "live-venue"];
const vibeOptions = ["rooftop", "live-music", "dj", "craft-beer", "vip", "casual", "premium", "cocktail-bar"];
const TABLE_ZONES = ["VIP", "Main Floor", "Rooftop", "Patio", "Bar Area", "Private Room"];
const CHART_COLORS = ["hsl(145, 80%, 55%)", "hsl(290, 60%, 65%)", "hsl(220, 75%, 65%)", "hsl(15, 85%, 60%)", "hsl(45, 90%, 55%)", "hsl(180, 60%, 50%)"];

// ─── Vendor Onboarding Tour ───
const TOUR_STEPS = [
  { target: "[data-tour-vendor='stats']", title: "Your Dashboard", desc: "Track bookings, revenue, and ratings at a glance." },
  { target: "[data-tour-vendor='bookings']", title: "Booking Management", desc: "Approve, assign tables, and manage all incoming bookings." },
  { target: "[data-tour-vendor='analytics']", title: "Revenue Analytics", desc: "Deep dive into revenue trends, peak days, and group sizes." },
  { target: "[data-tour-vendor='tables']", title: "Table Allocation", desc: "Manage table zones and see tonight's floor plan." },
  { target: "[data-tour-vendor='settings']", title: "Venue Settings", desc: "Edit your venue details, hours, and visibility." },
];

const VendorTour = ({ onComplete }: { onComplete: () => void }) => {
  const [step, setStep] = useState(0);
  const current = TOUR_STEPS[step];
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <motion.div key={step} initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="w-full max-w-sm rounded-2xl border border-primary/30 bg-card p-6 text-center">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10">
          <Sparkles className="h-7 w-7 text-primary" />
        </div>
        <h3 className="mb-2 text-lg font-bold text-foreground">{current.title}</h3>
        <p className="mb-6 text-sm text-muted-foreground">{current.desc}</p>
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">{step + 1} / {TOUR_STEPS.length}</span>
          <button onClick={() => step < TOUR_STEPS.length - 1 ? setStep(step + 1) : onComplete()}
            className="flex items-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground">
            {step < TOUR_STEPS.length - 1 ? <>Next <ChevronRight className="h-4 w-4" /></> : "Get Started"}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ─── Stat Card ───
const StatCard = ({ icon: Icon, value, label, trend, color, bg }: any) => (
  <div className="rounded-xl border border-border bg-card p-4">
    <div className="mb-2 flex items-center justify-between">
      <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${bg}`}>
        <Icon className={`h-4 w-4 ${color}`} />
      </div>
      {trend !== undefined && (
        <span className={`flex items-center gap-0.5 text-xs font-medium ${trend >= 0 ? "text-green-400" : "text-destructive"}`}>
          <TrendingUp className={`h-3 w-3 ${trend < 0 ? "rotate-180" : ""}`} />
          {Math.abs(trend)}%
        </span>
      )}
    </div>
    <p className="text-xl font-bold text-foreground">{value}</p>
    <p className="text-xs text-muted-foreground">{label}</p>
  </div>
);

const VendorPortal = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [venues, setVenues] = useState<any[]>([]);
  const [selectedVenue, setSelectedVenue] = useState<any>(null);
  const [bookings, setBookings] = useState<any[]>([]);
  const [reviews, setReviews] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState<any>({});
  const [tab, setTab] = useState<"overview" | "bookings" | "analytics" | "tables" | "reviews" | "settings">("overview");
  const [showAddVenue, setShowAddVenue] = useState(false);
  const [showTour, setShowTour] = useState(false);
  const [bookingFilter, setBookingFilter] = useState<"all" | "confirmed" | "pending" | "cancelled">("all");
  const [newVenue, setNewVenue] = useState({
    name: "", slug: "", address: "", city: "Bangalore", area: "",
    category: "lounge", capacity: 100, avg_price_per_person: 2000,
    description: "", vibe: [] as string[], opening_time: "18:00", closing_time: "02:00",
  });

  useEffect(() => {
    if (!user) return;
    const fetchVenues = async () => {
      const { data } = await supabase.from("venues").select("*").eq("owner_user_id", user.id);
      if (data && data.length > 0) {
        setVenues(data);
        setSelectedVenue(data[0]);
        // Check if vendor has seen tour
        const tourSeen = localStorage.getItem(`vendor-tour-${user.id}`);
        if (!tourSeen) setShowTour(true);
      }
      setLoading(false);
    };
    fetchVenues();
  }, [user]);

  useEffect(() => {
    if (!selectedVenue) return;
    const fetchDetails = async () => {
      const [bookingsRes, reviewsRes] = await Promise.all([
        supabase.from("bookings").select("*, krews(title, creator_id, max_members), venues(name)").eq("venue_id", selectedVenue.id).order("booking_date", { ascending: false }),
        supabase.from("venue_reviews").select("*, profiles:user_id(display_name)").eq("venue_id", selectedVenue.id).order("created_at", { ascending: false }),
      ]);
      if (bookingsRes.data) setBookings(bookingsRes.data);
      if (reviewsRes.data) setReviews(reviewsRes.data);
    };
    fetchDetails();

    const channel = supabase.channel(`vendor-${selectedVenue.id}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "bookings", filter: `venue_id=eq.${selectedVenue.id}` }, async () => {
        const { data } = await supabase.from("bookings").select("*, krews(title, creator_id, max_members), venues(name)").eq("venue_id", selectedVenue.id).order("booking_date", { ascending: false });
        if (data) setBookings(data);
      })
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "venue_reviews", filter: `venue_id=eq.${selectedVenue.id}` }, async () => {
        const { data } = await supabase.from("venue_reviews").select("*, profiles:user_id(display_name)").eq("venue_id", selectedVenue.id).order("created_at", { ascending: false });
        if (data) setReviews(data);
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [selectedVenue]);

  const handleAddVenue = async () => {
    if (!user || !newVenue.name || !newVenue.address) return;
    const slug = newVenue.slug || newVenue.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
    const { data, error } = await supabase.from("venues").insert({ ...newVenue, slug, owner_user_id: user.id }).select().single();
    if (error) { toast({ title: "Error", description: error.message, variant: "destructive" }); return; }
    setVenues(prev => [...prev, data]);
    setSelectedVenue(data);
    setShowAddVenue(false);
    setNewVenue({ name: "", slug: "", address: "", city: "Bangalore", area: "", category: "lounge", capacity: 100, avg_price_per_person: 2000, description: "", vibe: [], opening_time: "18:00", closing_time: "02:00" });
    toast({ title: "Venue added! 🎉", description: "Your venue is now live on Krew." });
    setShowTour(true);
  };

  const saveEdit = async () => {
    if (!selectedVenue) return;
    const { error } = await supabase.from("venues").update(editForm).eq("id", selectedVenue.id);
    if (error) { toast({ title: "Error", description: error.message, variant: "destructive" }); return; }
    setSelectedVenue({ ...selectedVenue, ...editForm });
    setVenues(prev => prev.map(v => v.id === selectedVenue.id ? { ...v, ...editForm } : v));
    setEditing(false);
    toast({ title: "Venue updated! ✅" });
  };

  const updateBookingStatus = async (bookingId: string, status: string) => {
    const { error } = await supabase.from("bookings").update({ status }).eq("id", bookingId);
    if (error) { toast({ title: "Error", description: error.message, variant: "destructive" }); return; }
    setBookings(prev => prev.map(b => b.id === bookingId ? { ...b, status } : b));
    toast({ title: `Booking ${status}` });
  };

  const assignTable = async (bookingId: string, zone: string, tableNumber: string) => {
    const { error } = await supabase.from("bookings").update({ table_zone: zone, table_number: tableNumber }).eq("id", bookingId);
    if (error) { toast({ title: "Error", description: error.message, variant: "destructive" }); return; }
    setBookings(prev => prev.map(b => b.id === bookingId ? { ...b, table_zone: zone, table_number: tableNumber } : b));
    toast({ title: `Table assigned: ${zone} — ${tableNumber}` });
  };

  const toggleVibe = (vibe: string) => {
    setNewVenue(prev => ({ ...prev, vibe: prev.vibe.includes(vibe) ? prev.vibe.filter(v => v !== vibe) : [...prev.vibe, vibe] }));
  };

  // ─── Analytics Data ───
  const confirmedBookings = bookings.filter(b => b.status === "confirmed");
  const todayBookings = bookings.filter(b => b.booking_date === format(new Date(), "yyyy-MM-dd"));
  const upcomingBookings = bookings.filter(b => isFuture(parseISO(b.booking_date)) && b.status === "confirmed");
  const totalRevenue = confirmedBookings.reduce((sum, b) => sum + (b.total_amount || 0), 0);
  const avgRating = reviews.length > 0 ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1) : "N/A";

  // Revenue by day (last 30 days)
  const revenueByDay = useMemo(() => {
    const days = eachDayOfInterval({ start: subDays(new Date(), 29), end: new Date() });
    return days.map(d => {
      const key = format(d, "yyyy-MM-dd");
      const dayBookings = confirmedBookings.filter(b => b.booking_date === key);
      return { date: format(d, "MMM d"), revenue: dayBookings.reduce((s, b) => s + (b.total_amount || 0), 0), count: dayBookings.length };
    });
  }, [confirmedBookings]);

  // Bookings by day of week
  const bookingsByDow = useMemo(() => {
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const counts = Array.from({ length: 7 }, () => 0);
    confirmedBookings.forEach(b => { counts[new Date(b.booking_date).getDay()]++; });
    return days.map((d, i) => ({ day: d, count: counts[i] }));
  }, [confirmedBookings]);

  // Group size distribution
  const groupSizeData = useMemo(() => {
    const buckets: Record<string, number> = { "1-4": 0, "5-8": 0, "9-12": 0, "13+": 0 };
    confirmedBookings.forEach(b => {
      const s = b.party_size || 0;
      if (s <= 4) buckets["1-4"]++;
      else if (s <= 8) buckets["5-8"]++;
      else if (s <= 12) buckets["9-12"]++;
      else buckets["13+"]++;
    });
    return Object.entries(buckets).map(([name, value]) => ({ name, value }));
  }, [confirmedBookings]);

  // Table zone usage
  const tableZoneData = useMemo(() => {
    const zones: Record<string, number> = {};
    confirmedBookings.forEach(b => {
      const z = b.table_zone || "main";
      zones[z] = (zones[z] || 0) + 1;
    });
    return Object.entries(zones).map(([name, value]) => ({ name, value }));
  }, [confirmedBookings]);

  // Revenue trends
  const thisMonthRevenue = confirmedBookings.filter(b => {
    const d = parseISO(b.booking_date);
    return d >= startOfMonth(new Date()) && d <= endOfMonth(new Date());
  }).reduce((s, b) => s + (b.total_amount || 0), 0);

  const lastMonthStart = startOfMonth(subDays(startOfMonth(new Date()), 1));
  const lastMonthEnd = endOfMonth(lastMonthStart);
  const lastMonthRevenue = confirmedBookings.filter(b => {
    const d = parseISO(b.booking_date);
    return d >= lastMonthStart && d <= lastMonthEnd;
  }).reduce((s, b) => s + (b.total_amount || 0), 0);

  const revenueTrend = lastMonthRevenue > 0 ? Math.round(((thisMonthRevenue - lastMonthRevenue) / lastMonthRevenue) * 100) : 0;

  const filteredBookings = bookingFilter === "all" ? bookings : bookings.filter(b => b.status === bookingFilter);

  const completeTour = () => {
    setShowTour(false);
    if (user) localStorage.setItem(`vendor-tour-${user.id}`, "true");
  };

  if (loading) return (
    <div className="flex min-h-[50vh] items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
    </div>
  );

  // ─── Onboarding: No Venues ───
  if (venues.length === 0 && !showAddVenue) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-md text-center">
          <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-3xl bg-primary/10">
            <Store className="h-10 w-10 text-primary" />
          </div>
          <h1 className="mb-2 text-3xl font-bold text-foreground">Welcome to Krew for <span className="text-gradient-neon">Venues</span></h1>
          <p className="mb-2 text-muted-foreground font-body">Get commitment-backed group bookings. No more no-shows. Higher spend per table.</p>
          <div className="mb-8 grid grid-cols-3 gap-3 text-center">
            {[
              { icon: Users, label: "Pre-committed groups", value: "Zero no-shows" },
              { icon: DollarSign, label: "Higher table spend", value: "Groups spend more" },
              { icon: BarChart3, label: "Real-time analytics", value: "Full visibility" },
            ].map(s => (
              <div key={s.label} className="rounded-xl border border-border bg-card p-3">
                <s.icon className="mx-auto mb-1 h-5 w-5 text-primary" />
                <p className="text-xs font-semibold text-foreground">{s.value}</p>
                <p className="text-[10px] text-muted-foreground">{s.label}</p>
              </div>
            ))}
          </div>
          <button onClick={() => setShowAddVenue(true)} className="inline-flex items-center gap-2 rounded-xl bg-primary px-8 py-3.5 text-sm font-semibold text-primary-foreground glow-neon hover:scale-[1.02] transition-all">
            <Plus className="h-4 w-4" /> Add Your Venue
          </button>
        </motion.div>
      </div>
    );
  }

  // ─── Add Venue Form ───
  if (showAddVenue) {
    return (
      <div className="mx-auto max-w-lg">
        <button onClick={() => setShowAddVenue(false)} className="mb-4 flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> Back</button>
        <h1 className="mb-6 text-2xl font-bold text-foreground">Add Your <span className="text-gradient-neon">Venue</span></h1>
        <div className="space-y-4">
          {[
            { label: "Venue Name *", key: "name", placeholder: "e.g. Skyye Lounge" },
            { label: "Full Address *", key: "address", placeholder: "Full address with pincode" },
            { label: "Area / Locality", key: "area", placeholder: "e.g. Indiranagar, Bandra West" },
          ].map(({ label, key, placeholder }) => (
            <div key={key}>
              <label className="mb-1.5 block text-sm font-medium text-foreground">{label}</label>
              <input value={(newVenue as any)[key]} onChange={(e) => setNewVenue(prev => ({ ...prev, [key]: e.target.value }))} placeholder={placeholder}
                className="w-full rounded-xl border border-border bg-muted px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary" />
            </div>
          ))}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-foreground">City</label>
              <input list="city-list" value={newVenue.city} onChange={(e) => setNewVenue(prev => ({ ...prev, city: e.target.value }))} placeholder="Type or select city"
                className="w-full rounded-xl border border-border bg-muted px-4 py-3 text-sm text-foreground focus:border-primary focus:outline-none" />
              <datalist id="city-list">{allCities.map(c => <option key={c} value={c} />)}</datalist>
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-foreground">Category</label>
              <select value={newVenue.category} onChange={(e) => setNewVenue(prev => ({ ...prev, category: e.target.value }))}
                className="w-full rounded-xl border border-border bg-muted px-4 py-3 text-sm text-foreground focus:border-primary focus:outline-none">
                {categories.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-foreground">Capacity</label>
              <input type="number" value={newVenue.capacity} onChange={(e) => setNewVenue(prev => ({ ...prev, capacity: +e.target.value }))}
                className="w-full rounded-xl border border-border bg-muted px-4 py-3 text-sm text-foreground focus:border-primary focus:outline-none" />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-foreground">Avg ₹/person</label>
              <input type="number" value={newVenue.avg_price_per_person} onChange={(e) => setNewVenue(prev => ({ ...prev, avg_price_per_person: +e.target.value }))}
                className="w-full rounded-xl border border-border bg-muted px-4 py-3 text-sm text-foreground focus:border-primary focus:outline-none" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-foreground">Opens</label>
              <input type="time" value={newVenue.opening_time} onChange={(e) => setNewVenue(prev => ({ ...prev, opening_time: e.target.value }))}
                className="w-full rounded-xl border border-border bg-muted px-4 py-3 text-sm text-foreground focus:border-primary focus:outline-none" />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-foreground">Closes</label>
              <input type="time" value={newVenue.closing_time} onChange={(e) => setNewVenue(prev => ({ ...prev, closing_time: e.target.value }))}
                className="w-full rounded-xl border border-border bg-muted px-4 py-3 text-sm text-foreground focus:border-primary focus:outline-none" />
            </div>
          </div>
          <div>
            <label className="mb-2 block text-sm font-medium text-foreground">Vibes</label>
            <div className="flex flex-wrap gap-2">
              {vibeOptions.map(v => (
                <button key={v} type="button" onClick={() => toggleVibe(v)}
                  className={`rounded-full px-3 py-1.5 text-xs font-medium transition-all ${newVenue.vibe.includes(v) ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:text-foreground"}`}>{v}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground">Description</label>
            <textarea value={newVenue.description} onChange={(e) => setNewVenue(prev => ({ ...prev, description: e.target.value }))} rows={3} placeholder="Describe your venue"
              className="w-full rounded-xl border border-border bg-muted px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none resize-none" />
          </div>
          <button onClick={handleAddVenue} disabled={!newVenue.name || !newVenue.address}
            className="w-full rounded-xl bg-primary px-6 py-3.5 text-sm font-semibold text-primary-foreground glow-neon hover:scale-[1.01] transition-all disabled:opacity-50">
            <Sparkles className="mr-2 inline h-4 w-4" /> Add Venue & Go Live
          </button>
        </div>
      </div>
    );
  }

  const tabs = [
    { key: "overview" as const, label: "Overview", icon: Eye },
    { key: "bookings" as const, label: "Bookings", icon: Calendar },
    { key: "analytics" as const, label: "Analytics", icon: BarChart3 },
    { key: "tables" as const, label: "Tables", icon: Grid3X3 },
    { key: "reviews" as const, label: "Reviews", icon: Star },
    { key: "settings" as const, label: "Settings", icon: Edit3 },
  ];

  return (
    <div className="space-y-6">
      {/* Tour */}
      <AnimatePresence>{showTour && <VendorTour onComplete={completeTour} />}</AnimatePresence>

      {/* Venue Selector */}
      {venues.length > 1 && (
        <div className="flex flex-wrap gap-2">
          {venues.map(v => (
            <button key={v.id} onClick={() => setSelectedVenue(v)}
              className={`rounded-full px-4 py-2 text-xs font-medium transition-all ${selectedVenue?.id === v.id ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:text-foreground"}`}>{v.name}</button>
          ))}
          <button onClick={() => setShowAddVenue(true)} className="rounded-full border border-dashed border-border px-4 py-2 text-xs text-muted-foreground hover:text-foreground">
            <Plus className="inline h-3 w-3" /> Add
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground sm:text-3xl">{selectedVenue?.name}</h1>
          <p className="text-sm text-muted-foreground">{selectedVenue?.area}, {selectedVenue?.city} · {selectedVenue?.category}</p>
        </div>
        <div className="flex gap-2">
          <Link to={`/venue/${selectedVenue?.slug}`} className="flex items-center gap-2 rounded-xl border border-border px-4 py-2.5 text-sm text-muted-foreground hover:text-foreground transition-all">
            <Eye className="h-4 w-4" /> View Public Page
          </Link>
          {venues.length <= 1 && (
            <button onClick={() => setShowAddVenue(true)} className="flex items-center gap-2 rounded-xl border border-primary/30 bg-primary/10 px-4 py-2.5 text-sm text-primary hover:bg-primary/20 transition-all">
              <Plus className="h-4 w-4" /> Add Venue
            </button>
          )}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6" data-tour-vendor="stats">
        <StatCard icon={Calendar} value={todayBookings.length} label="Today" color="text-primary" bg="bg-primary/10" />
        <StatCard icon={Users} value={upcomingBookings.reduce((s, b) => s + b.party_size, 0)} label="Expected Guests" color="text-secondary" bg="bg-secondary/10" />
        <StatCard icon={DollarSign} value={`₹${(thisMonthRevenue / 1000).toFixed(0)}K`} label="This Month" color="text-green-400" bg="bg-green-500/10" trend={revenueTrend} />
        <StatCard icon={Star} value={avgRating} label={`Rating (${reviews.length})`} color="text-secondary" bg="bg-secondary/10" />
        <StatCard icon={Zap} value={confirmedBookings.length} label="Total Bookings" color="text-primary" bg="bg-primary/10" />
        <StatCard icon={Bell} value={bookings.filter(b => b.status === "pending").length} label="Pending" color="text-orange-400" bg="bg-orange-500/10" />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 overflow-x-auto rounded-xl border border-border bg-card p-1">
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} data-tour-vendor={t.key}
            className={`flex min-w-0 flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg py-2.5 text-sm font-medium capitalize transition-all ${tab === t.key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
            <t.icon className="h-4 w-4 hidden sm:block" />{t.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        <motion.div key={tab} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>

          {/* ─── OVERVIEW ─── */}
          {tab === "overview" && (
            <div className="space-y-6">
              {/* Revenue Chart */}
              <div className="rounded-xl border border-border bg-card p-5">
                <h3 className="mb-4 text-lg font-bold text-foreground">Revenue — Last 30 Days</h3>
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={revenueByDay}>
                      <XAxis dataKey="date" tick={{ fill: "hsl(240, 8%, 55%)", fontSize: 10 }} tickLine={false} axisLine={false} interval={6} />
                      <YAxis tick={{ fill: "hsl(240, 8%, 55%)", fontSize: 10 }} tickLine={false} axisLine={false} tickFormatter={v => `₹${v/1000}K`} />
                      <Tooltip contentStyle={{ backgroundColor: "hsl(240, 12%, 8%)", border: "1px solid hsl(240, 10%, 16%)", borderRadius: 12, color: "hsl(210, 20%, 95%)" }} formatter={(v: any) => [`₹${v.toLocaleString()}`, "Revenue"]} />
                      <Bar dataKey="revenue" fill="hsl(145, 80%, 55%)" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Today's Schedule */}
              <div>
                <h3 className="mb-3 text-lg font-bold text-foreground">Today's Schedule</h3>
                {todayBookings.length > 0 ? (
                  <div className="space-y-3">
                    {todayBookings.map(b => (
                      <div key={b.id} className="flex items-center gap-4 rounded-xl border border-border bg-card p-4">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                          <Clock className="h-5 w-5 text-primary" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-foreground">{(b.krews as any)?.title || "Booking"}</p>
                          <p className="text-xs text-muted-foreground">{b.booking_time?.slice(0, 5)} · {b.party_size} guests · {b.table_zone || "Main"} {b.table_number ? `— ${b.table_number}` : ""}</p>
                        </div>
                        <span className={`rounded-full px-3 py-1 text-xs font-medium ${b.status === "confirmed" ? "bg-green-500/20 text-green-400" : b.status === "cancelled" ? "bg-destructive/20 text-destructive" : "bg-secondary/20 text-secondary"}`}>
                          {b.status}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No bookings for today.</p>
                )}
              </div>

              {/* Recent Reviews */}
              <div>
                <h3 className="mb-3 text-lg font-bold text-foreground">Recent Reviews</h3>
                {reviews.slice(0, 3).map(r => (
                  <div key={r.id} className="mb-2 rounded-xl border border-border bg-card p-4">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-semibold text-foreground">{(r.profiles as any)?.display_name}</span>
                      <div className="flex gap-0.5">{[1,2,3,4,5].map(s => <Star key={s} className={`h-3 w-3 ${s <= r.rating ? "fill-secondary text-secondary" : "text-muted"}`} />)}</div>
                    </div>
                    {r.review && <p className="text-xs text-muted-foreground">{r.review}</p>}
                  </div>
                ))}
                {reviews.length === 0 && <p className="text-sm text-muted-foreground">No reviews yet.</p>}
              </div>
            </div>
          )}

          {/* ─── BOOKINGS ─── */}
          {tab === "bookings" && (
            <div className="space-y-4">
              {/* Filters */}
              <div className="flex flex-wrap gap-2">
                {(["all", "pending", "confirmed", "cancelled"] as const).map(f => (
                  <button key={f} onClick={() => setBookingFilter(f)}
                    className={`rounded-full px-4 py-2 text-xs font-medium capitalize transition-all ${bookingFilter === f ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:text-foreground"}`}>{f}</button>
                ))}
              </div>

              {filteredBookings.length === 0 ? (
                <p className="py-8 text-center text-muted-foreground">No {bookingFilter !== "all" ? bookingFilter : ""} bookings.</p>
              ) : filteredBookings.map(b => (
                <div key={b.id} className="rounded-xl border border-border bg-card p-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-semibold text-foreground">{(b.krews as any)?.title || "Direct Booking"}</p>
                        <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-medium ${b.status === "confirmed" ? "bg-green-500/20 text-green-400" : b.status === "cancelled" ? "bg-destructive/20 text-destructive" : "bg-orange-500/20 text-orange-400"}`}>{b.status}</span>
                      </div>
                      <p className="text-xs text-muted-foreground">{format(new Date(b.booking_date), "EEE, MMM d")} at {b.booking_time?.slice(0, 5)} · {b.party_size} guests</p>
                      <p className="text-xs text-muted-foreground">₹{b.total_amount?.toLocaleString()} · Code: <span className="font-mono text-primary">{b.confirmation_code}</span></p>
                      {b.table_zone && <p className="text-xs text-primary mt-1"><Table2 className="inline h-3 w-3 mr-1" />{b.table_zone} {b.table_number ? `— Table ${b.table_number}` : ""}</p>}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {/* Table Assignment */}
                      {b.status === "confirmed" && (
                        <select value={b.table_zone || "main"} onChange={(e) => {
                          const zone = e.target.value;
                          const num = prompt(`Table number for ${zone}?`, b.table_number || "");
                          if (num !== null) assignTable(b.id, zone, num);
                        }} className="rounded-lg border border-border bg-muted px-2 py-1.5 text-xs text-foreground">
                          {TABLE_ZONES.map(z => <option key={z} value={z}>{z}</option>)}
                        </select>
                      )}
                      {b.status === "confirmed" && (
                        <button onClick={() => updateBookingStatus(b.id, "cancelled")} className="rounded-lg bg-destructive/10 p-2 text-destructive hover:bg-destructive/20" title="Cancel"><X className="h-4 w-4" /></button>
                      )}
                      {b.status === "pending" && (
                        <div className="flex gap-1">
                          <button onClick={() => updateBookingStatus(b.id, "confirmed")} className="rounded-lg bg-green-500/10 p-2 text-green-400 hover:bg-green-500/20" title="Confirm"><Check className="h-4 w-4" /></button>
                          <button onClick={() => updateBookingStatus(b.id, "cancelled")} className="rounded-lg bg-destructive/10 p-2 text-destructive hover:bg-destructive/20" title="Decline"><X className="h-4 w-4" /></button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ─── ANALYTICS ─── */}
          {tab === "analytics" && (
            <div className="space-y-6" data-tour-vendor="analytics">
              {/* KPIs */}
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <div className="rounded-xl border border-border bg-card p-4 text-center">
                  <p className="text-2xl font-bold text-foreground">₹{(totalRevenue / 1000).toFixed(0)}K</p>
                  <p className="text-xs text-muted-foreground">All-time Revenue</p>
                </div>
                <div className="rounded-xl border border-border bg-card p-4 text-center">
                  <p className="text-2xl font-bold text-foreground">{confirmedBookings.length > 0 ? Math.round(confirmedBookings.reduce((s, b) => s + b.party_size, 0) / confirmedBookings.length) : 0}</p>
                  <p className="text-xs text-muted-foreground">Avg Group Size</p>
                </div>
                <div className="rounded-xl border border-border bg-card p-4 text-center">
                  <p className="text-2xl font-bold text-foreground">{confirmedBookings.length > 0 ? `₹${Math.round(totalRevenue / confirmedBookings.length).toLocaleString()}` : "₹0"}</p>
                  <p className="text-xs text-muted-foreground">Avg Booking Value</p>
                </div>
                <div className="rounded-xl border border-border bg-card p-4 text-center">
                  <p className="text-2xl font-bold text-foreground">{bookings.filter(b => b.status === "cancelled").length}</p>
                  <p className="text-xs text-muted-foreground">Cancellations</p>
                </div>
              </div>

              {/* Revenue Trend */}
              <div className="rounded-xl border border-border bg-card p-5">
                <h3 className="mb-4 font-bold text-foreground">Revenue Trend — 30 Days</h3>
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={revenueByDay}>
                      <XAxis dataKey="date" tick={{ fill: "hsl(240, 8%, 55%)", fontSize: 10 }} tickLine={false} axisLine={false} interval={6} />
                      <YAxis tick={{ fill: "hsl(240, 8%, 55%)", fontSize: 10 }} tickLine={false} axisLine={false} tickFormatter={v => `₹${v/1000}K`} />
                      <Tooltip contentStyle={{ backgroundColor: "hsl(240, 12%, 8%)", border: "1px solid hsl(240, 10%, 16%)", borderRadius: 12, color: "hsl(210, 20%, 95%)" }} formatter={(v: any) => [`₹${v.toLocaleString()}`, "Revenue"]} />
                      <Line type="monotone" dataKey="revenue" stroke="hsl(145, 80%, 55%)" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Charts Row */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {/* Bookings by Day of Week */}
                <div className="rounded-xl border border-border bg-card p-5">
                  <h3 className="mb-4 font-bold text-foreground">Peak Days</h3>
                  <div className="h-40">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={bookingsByDow}>
                        <XAxis dataKey="day" tick={{ fill: "hsl(240, 8%, 55%)", fontSize: 10 }} tickLine={false} axisLine={false} />
                        <YAxis tick={{ fill: "hsl(240, 8%, 55%)", fontSize: 10 }} tickLine={false} axisLine={false} />
                        <Bar dataKey="count" fill="hsl(290, 60%, 65%)" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Group Size Distribution */}
                <div className="rounded-xl border border-border bg-card p-5">
                  <h3 className="mb-4 font-bold text-foreground">Group Sizes</h3>
                  <div className="h-40">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={groupSizeData} dataKey="value" cx="50%" cy="50%" outerRadius={60} label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}>
                          {groupSizeData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Table Zone Usage */}
                <div className="rounded-xl border border-border bg-card p-5">
                  <h3 className="mb-4 font-bold text-foreground">Zone Usage</h3>
                  <div className="h-40">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={tableZoneData} dataKey="value" cx="50%" cy="50%" outerRadius={60} label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}>
                          {tableZoneData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ─── TABLE ALLOCATION ─── */}
          {tab === "tables" && (
            <div className="space-y-6" data-tour-vendor="tables">
              <div className="rounded-xl border border-border bg-card p-5">
                <h3 className="mb-4 font-bold text-foreground">Tonight's Floor Plan</h3>
                <p className="mb-4 text-sm text-muted-foreground">Capacity: {selectedVenue?.capacity} · {todayBookings.filter(b => b.status === "confirmed").reduce((s, b) => s + b.party_size, 0)} guests expected</p>

                {/* Capacity bar */}
                <div className="mb-6 rounded-full bg-muted h-3 overflow-hidden">
                  <div className="h-full rounded-full bg-primary transition-all" style={{
                    width: `${Math.min(100, (todayBookings.filter(b => b.status === "confirmed").reduce((s, b) => s + b.party_size, 0) / (selectedVenue?.capacity || 100)) * 100)}%`
                  }} />
                </div>

                {/* Zone grid */}
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  {TABLE_ZONES.map(zone => {
                    const zoneBookings = todayBookings.filter(b => b.table_zone === zone && b.status === "confirmed");
                    const zoneGuests = zoneBookings.reduce((s, b) => s + b.party_size, 0);
                    return (
                      <div key={zone} className={`rounded-xl border p-4 transition-all ${zoneBookings.length > 0 ? "border-primary/30 bg-primary/5" : "border-border bg-card"}`}>
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-sm font-semibold text-foreground">{zone}</h4>
                          {zoneBookings.length > 0 && <span className="rounded-full bg-primary/20 px-2 py-0.5 text-[10px] font-bold text-primary">{zoneBookings.length}</span>}
                        </div>
                        <p className="text-xs text-muted-foreground">{zoneGuests} guests</p>
                        {zoneBookings.map(b => (
                          <div key={b.id} className="mt-2 rounded-lg bg-muted p-2">
                            <p className="text-xs font-medium text-foreground">{(b.krews as any)?.title || "Booking"}</p>
                            <p className="text-[10px] text-muted-foreground">{b.booking_time?.slice(0, 5)} · {b.party_size}p {b.table_number ? `· T${b.table_number}` : ""}</p>
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Upcoming with table assignment */}
              <div>
                <h3 className="mb-3 font-bold text-foreground">Upcoming — Assign Tables</h3>
                {upcomingBookings.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No upcoming bookings to assign.</p>
                ) : upcomingBookings.slice(0, 10).map(b => (
                  <div key={b.id} className="mb-2 flex items-center gap-3 rounded-xl border border-border bg-card p-4">
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-foreground">{(b.krews as any)?.title || "Booking"}</p>
                      <p className="text-xs text-muted-foreground">{format(new Date(b.booking_date), "EEE, MMM d")} · {b.party_size}p</p>
                    </div>
                    <select value={b.table_zone || "Main Floor"} onChange={(e) => {
                      const zone = e.target.value;
                      const num = prompt(`Table number for ${zone}?`, b.table_number || "");
                      if (num !== null) assignTable(b.id, zone, num);
                    }} className="rounded-lg border border-border bg-muted px-3 py-1.5 text-xs text-foreground">
                      {TABLE_ZONES.map(z => <option key={z} value={z}>{z}</option>)}
                    </select>
                    <span className="text-xs text-muted-foreground">{b.table_number ? `T${b.table_number}` : "—"}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ─── REVIEWS ─── */}
          {tab === "reviews" && (
            <div className="space-y-3">
              <div className="mb-4 rounded-xl border border-secondary/20 bg-secondary/5 p-4 text-center">
                <p className="text-3xl font-bold text-foreground">{avgRating}</p>
                <div className="my-1 flex justify-center gap-0.5">{[1,2,3,4,5].map(s => <Star key={s} className={`h-5 w-5 ${s <= Math.round(Number(avgRating)) ? "fill-secondary text-secondary" : "text-muted"}`} />)}</div>
                <p className="text-xs text-muted-foreground">{reviews.length} total reviews</p>
              </div>
              {reviews.map(r => (
                <div key={r.id} className="rounded-xl border border-border bg-card p-4">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-semibold text-foreground">{(r.profiles as any)?.display_name}</span>
                    <div className="flex items-center gap-2">
                      <div className="flex gap-0.5">{[1,2,3,4,5].map(s => <Star key={s} className={`h-3 w-3 ${s <= r.rating ? "fill-secondary text-secondary" : "text-muted"}`} />)}</div>
                      <span className="text-xs text-muted-foreground">{format(new Date(r.created_at), "MMM d")}</span>
                    </div>
                  </div>
                  {r.review && <p className="text-sm text-muted-foreground">{r.review}</p>}
                </div>
              ))}
              {reviews.length === 0 && <p className="py-8 text-center text-muted-foreground">No reviews yet.</p>}
            </div>
          )}

          {/* ─── SETTINGS ─── */}
          {tab === "settings" && selectedVenue && (
            <div className="space-y-4" data-tour-vendor="settings">
              {editing ? (
                <>
                  {[
                    { label: "Name", key: "name" },
                    { label: "Description", key: "description", type: "textarea" },
                    { label: "Address", key: "address" },
                    { label: "Area", key: "area" },
                  ].map(({ label, key, type }) => (
                    <div key={key}>
                      <label className="mb-1.5 block text-sm font-medium text-foreground">{label}</label>
                      {type === "textarea" ? (
                        <textarea value={editForm[key] ?? selectedVenue[key] ?? ""} onChange={(e) => setEditForm((p: any) => ({ ...p, [key]: e.target.value }))} rows={3}
                          className="w-full rounded-xl border border-border bg-muted px-4 py-3 text-sm text-foreground focus:border-primary focus:outline-none resize-none" />
                      ) : (
                        <input value={editForm[key] ?? selectedVenue[key] ?? ""} onChange={(e) => setEditForm((p: any) => ({ ...p, [key]: e.target.value }))}
                          className="w-full rounded-xl border border-border bg-muted px-4 py-3 text-sm text-foreground focus:border-primary focus:outline-none" />
                      )}
                    </div>
                  ))}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-foreground">Capacity</label>
                      <input type="number" value={editForm.capacity ?? selectedVenue.capacity} onChange={(e) => setEditForm((p: any) => ({ ...p, capacity: +e.target.value }))}
                        className="w-full rounded-xl border border-border bg-muted px-4 py-3 text-sm text-foreground focus:border-primary focus:outline-none" />
                    </div>
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-foreground">Avg ₹/person</label>
                      <input type="number" value={editForm.avg_price_per_person ?? selectedVenue.avg_price_per_person} onChange={(e) => setEditForm((p: any) => ({ ...p, avg_price_per_person: +e.target.value }))}
                        className="w-full rounded-xl border border-border bg-muted px-4 py-3 text-sm text-foreground focus:border-primary focus:outline-none" />
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <button onClick={saveEdit} className="flex items-center gap-2 rounded-xl bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground"><Save className="h-4 w-4" /> Save</button>
                    <button onClick={() => { setEditing(false); setEditForm({}); }} className="rounded-xl border border-border px-6 py-3 text-sm text-muted-foreground hover:text-foreground">Cancel</button>
                  </div>
                </>
              ) : (
                <div className="space-y-4">
                  <div className="rounded-xl border border-border bg-card p-5">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-bold text-foreground">Venue Details</h3>
                      <button onClick={() => setEditing(true)} className="flex items-center gap-1 text-sm text-primary hover:underline"><Edit3 className="h-3.5 w-3.5" /> Edit</button>
                    </div>
                    <dl className="space-y-3 text-sm">
                      {[
                        ["Name", selectedVenue.name], ["Address", selectedVenue.address], ["Area", selectedVenue.area],
                        ["City", selectedVenue.city], ["Category", selectedVenue.category], ["Capacity", selectedVenue.capacity],
                        ["Avg Price/Person", `₹${selectedVenue.avg_price_per_person}`],
                        ["Hours", `${selectedVenue.opening_time?.slice(0, 5) || "18:00"} — ${selectedVenue.closing_time?.slice(0, 5) || "02:00"}`],
                        ["Vibes", (selectedVenue.vibe || []).join(", ") || "None set"],
                      ].map(([k, v]) => (
                        <div key={k as string} className="flex justify-between">
                          <dt className="text-muted-foreground">{k}</dt>
                          <dd className="font-medium text-foreground text-right max-w-[200px] truncate">{v}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                  <div className="rounded-xl border border-border bg-card p-5">
                    <h3 className="mb-2 font-bold text-foreground">Active Status</h3>
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-muted-foreground">Venue is {selectedVenue.is_active ? "visible" : "hidden"} to Krew users</p>
                      <button onClick={async () => {
                        const { error } = await supabase.from("venues").update({ is_active: !selectedVenue.is_active }).eq("id", selectedVenue.id);
                        if (!error) {
                          setSelectedVenue({ ...selectedVenue, is_active: !selectedVenue.is_active });
                          toast({ title: selectedVenue.is_active ? "Venue hidden" : "Venue is live!" });
                        }
                      }} className={`rounded-full px-4 py-1.5 text-xs font-medium ${selectedVenue.is_active ? "bg-green-500/20 text-green-400" : "bg-destructive/20 text-destructive"}`}>
                        {selectedVenue.is_active ? "Active" : "Inactive"}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default VendorPortal;
